import { JSONSchema7 } from "json-schema";
import { UiSchema } from "@rjsf/utils";
import { DEFAULT_WAITLIST_HINT, DEFAULT_WAITLIST_ZIP_CODE_MESSAGE } from "@webtie-ch/form-widget-library";

import { FormFieldDefinition } from "./fieldTypes";

const hasZipCodeWhitelist = (fields: FormFieldDefinition[]): boolean =>
  fields.some((field) => field.fieldType === "zipCode" && !!field.zipCodes?.trim());

/** Felder, die eine E-Mail-Adresse enthalten können — nur die taugen als Bestätigungsfeld. */
const isAddressableField = (field: FormFieldDefinition): boolean =>
  ["string", "email"].includes(field.fieldType) && !!field.title?.trim();

/** Hilfetexte unter den Checkboxen (rjsf zeigt `description` bei Checkboxen darüber an) und Platzhalter mit den Standardtexten. */
export const waitlistMetaUiSchema: UiSchema = {
  waitlistEnabled: {
    "ui:help":
      "Ausgebuchte Angebote bleiben wählbar und sind mit «(Warteliste)» markiert. Wer sie wählt, kommt dafür auf die Warteliste, freie Angebote werden normal angemeldet. Bei einer nicht erlaubten Postleitzahl ist nur die Warteliste möglich."
  },
  waitlistExcludedFromMaxOffers: {
    "ui:help":
      "Die maximale Anzahl an auswählbaren Angeboten gilt dann nur für feste Anmeldungen. Auf die Warteliste kann man sich für beliebig viele Angebote setzen."
  },
  waitlistHint: {
    "ui:widget": "textarea",
    "ui:placeholder": DEFAULT_WAITLIST_HINT,
    "ui:options": { rows: 2 }
  },
  waitlistZipCodeMessage: {
    "ui:widget": "textarea",
    "ui:placeholder": DEFAULT_WAITLIST_ZIP_CODE_MESSAGE,
    "ui:options": { rows: 2 }
  }
};

export interface FormMetaSchemas {
  general: JSONSchema7;
  registration: JSONSchema7;
  waitlist: JSONSchema7;
}

/**
 * Formular-Einstellungen ohne `formFields` — die Felder verwaltet FormFieldsEditor.
 * Aufgeteilt auf die Reiter Allgemein (Wurzel der Metadaten), Anmeldung und Warteliste (beide `formSettings`).
 * Das Bestätigungsfeld wird aus den aktuell vorhandenen Feldern aufgebaut.
 */
export const buildFormMetaSchemas = (fields: FormFieldDefinition[]): FormMetaSchemas => {
  const addressableFields = fields.filter(isAddressableField).map((field) => field.title);

  return {
    general: {
      type: "object",
      properties: {
        title: {
          type: "string",
          title: "Titel"
        },
        releaseDate: {
          type: "object",
          title: "Geplante Veröffentlichung",
          description:
            "Falls die Anmeldung zu einem späteren Zeitpunkt veröffentlicht werden soll, können Sie folgend das Datum und die Uhrzeit angeben.",
          properties: {
            dateTime: {
              type: "string",
              title: "Datum und Zeit",
              format: "date-time"
            },
            timeZone: {
              type: "string",
              title: "Zeitzone",
              default: "Europe/Zurich",
              oneOf: [{ const: "Europe/Zurich", title: "Europe/Zurich" }]
            }
          }
        }
      }
    },
    registration: {
      type: "object",
      properties: {
        emailConfirmationField: {
          type: "string",
          title: "Formularfeld mit der E-Mail-Adresse für das Bestätigungs-E-Mail",
          description:
            "Wählen Sie das Feld, in dem die Familie ihre E-Mail-Adresse einträgt. An diese Adresse geht die Bestätigung der Anmeldung. Ohne Auswahl wird kein Bestätigungs-E-Mail an die Familie versendet.",
          oneOf: [
            { const: "", title: "— Kein Bestätigungs-E-Mail —" },
            ...addressableFields.map((title) => ({ const: title, title }))
          ]
        },
        maxOffersToSelect: {
          type: "number",
          title: "Maximale Anzahl an auswählbaren Angeboten",
          description:
            "Geben Sie an, wie viele Angebote bei einer Anmeldung ausgewählt werden dürfen. Wenn Sie keine Limitierung möchten, lassen Sie das Feld leer oder geben Sie 0 ein.",
          minimum: 0,
          maximum: 99
        }
      }
    },
    waitlist: {
      type: "object",
      properties: {
        waitlistEnabled: {
          type: "boolean",
          title: "Warteliste für ausgebuchte Angebote aktivieren"
        }
      },
      dependencies: {
        waitlistEnabled: {
          oneOf: [
            { properties: { waitlistEnabled: { const: false } } },
            {
              properties: {
                waitlistEnabled: { const: true },
                waitlistExcludedFromMaxOffers: {
                  type: "boolean",
                  title: "Wartelisten-Angebote zählen nicht zur maximalen Anzahl"
                },
                waitlistHint: {
                  type: "string",
                  title: "Hinweis vor dem Absenden",
                  description:
                    "Erscheint beim Anmelden-Knopf, sobald ein Angebot auf die Warteliste kommt. Leer lassen für den Standardtext."
                },
                ...(hasZipCodeWhitelist(fields) && {
                  waitlistZipCodeMessage: {
                    type: "string",
                    title: "Meldung beim PLZ-Feld bei nicht erlaubter Postleitzahl",
                    description:
                      "Erscheint beim PLZ-Feld, wenn die Postleitzahl nicht erlaubt ist und deshalb nur die Warteliste möglich ist. Leer lassen für den Standardtext."
                  }
                })
              }
            }
          ]
        }
      }
    }
  };
};
